import * as axios from "axios";
import { stellarTolmResolver } from "../stellar/tolm";
import * as Sentry from "@sentry/react";
import { Preferences } from '@capacitor/preferences';
import { StellarConfiguration, Wallet } from "@stellar/typescript-wallet-sdk";


let jwtToken: string;

interface ResponseData<T = any> {
    data: T;
    status: number;
}

/**
 * Try to load cached JWT for an anchor domain.
 */
async function getJWT(anchor_domain: string): Promise<string> {
    const { value } = await Preferences.get({ key: anchor_domain });
    return value || "";
}

/**
 * Normalize axios response into a consistent object.
 */
function normalizeServerResponse(serverResponse: any): ResponseData {
    return {
        data: serverResponse?.data,
        status: serverResponse?.status,
    };
}

/**
 * Helper to resolve SEP-24 transfer server URL from TOML.
 */
async function resolveTransferServer(anchor_domain: string): Promise<string> {
    const [err, toml] = await stellarTolmResolver(anchor_domain);

    if (err) {
        Sentry.captureException(err);
        throw new Error(`Could not resolve TOML for ${anchor_domain}: ${err.message || err}`);
    }

    if (!toml?.TRANSFER_SERVER_SEP0024) {
        throw new Error(`No TRANSFER_SERVER_SEP0024 found in TOML for ${anchor_domain}`);
    }

    return toml.TRANSFER_SERVER_SEP0024;
}

/**
 * SEP-24 Deposit (Interactive)
 */
export async function initiateSep24Deposit(
    anchor_domain: string,
    asset_code: string,
    stellar_account: string,
    user_email?: string,
    user_mobile_number?: string,
    language?: string,
    token?: string,
    apiKey?: string,
    amount?: string
) {

    jwtToken = token || (await getJWT(anchor_domain));
    const transferServer = await resolveTransferServer(anchor_domain);

    try {
        let endpoint = `${transferServer}/transactions/deposit/interactive`;
        if (apiKey) endpoint += `?apiKey=${apiKey}`;

        const response = await axios.default.request({
            method: "post",
            url: endpoint,
            data: {
                asset_code,
                amount,
                email_address: user_email,
                account: stellar_account,
                mobile_number: user_mobile_number,
                lang: language,
            },
            headers: { Authorization: `Bearer ${jwtToken}` },
        });

        return normalizeServerResponse(response);
    } catch (error: any) {
        Sentry.captureException(error);

        const normalizedError = normalizeServerResponse(error?.response);
        const message = normalizedError?.data || error.message || "SEP24 deposit failed";
        throw new Error(message);
    }
}

/**
 * SEP-24 Withdraw (Interactive)
 */
export async function initiateWithdraw(
    anchor_domain: string,
    asset_code: string,
    stellar_account: string,
    user_email: string,
    user_mobile_number: string,
    language?: string,
    token?: string,
    apiKey?: string
): Promise<ResponseData> {
    jwtToken = token || (await getJWT(anchor_domain));
    const transferServer = await resolveTransferServer(anchor_domain);

    try {
        let endpoint = `${transferServer}/transactions/withdraw/interactive`;
        if (apiKey) endpoint += `?apiKey=${apiKey}`;

        const response = await axios.default.request({
            method: "post",
            url: endpoint,
            data: {
                asset_code,
                email_address: user_email,
                account: stellar_account,
                claimable_balance_supported: true,
                mobile_number: user_mobile_number,
                lang: language,
            },
            headers: { Authorization: `Bearer ${jwtToken}` },
        });

        return normalizeServerResponse(response);
    } catch (error: any) {
        Sentry.captureException(error);

        const normalizedError = normalizeServerResponse(error?.response);
        const message = normalizedError?.data || error.message || "SEP24 withdraw failed";
        throw new Error(message);
    }
}








